/**
 * Rules of the tick: things that happen to a board at the start of a tick,
 * before that tick's inputs, as a function of which tick it is.
 *
 * There is one today, the rising floor, and one implementation of it. Solo
 * survival, `duelSurvival` and `coopWide` all push a garbage row in on a
 * cadence, and all three do it through `risingFloorRule`, on the same ticks,
 * through the same engine primitive. `docs/decisions/0050`.
 *
 * Two properties make a rule safe to run in several simulations of one board:
 *
 * 1. **It reads the tick number, never a counter it keeps.** A driver rebuilt
 *    from a snapshot mid-round has no history; a cadence read off
 *    `tick % everyTicks` needs none.
 * 2. **It is not stepped against a topped-out board.** The engine does not
 *    step one either, and a floor that kept rising after a knock-out would
 *    move a board one end has stopped watching and the other has not.
 *    `applyTickRule` is where that is decided, once.
 */
import { applyGarbageLines, step } from '@interlock/engine';
import type { GameState, InputEvent, StepResult } from '@interlock/engine';

/**
 * A rule applied at the start of a tick, before that tick's inputs.
 *
 * `tick` is passed explicitly rather than read off the state so the contract
 * is impossible to misread: this is a function of *which tick it is*.
 */
export type TickRule = (state: GameState, tick: number) => void;

/**
 * The rising floor: one garbage row every `everyTicks` ticks.
 *
 * Tick 0 is the board as dealt; the first row arrives one full interval in,
 * and every row after it one interval after the last.
 *
 * The row is applied **directly**, with the engine's `applyGarbageLines`, and
 * not queued through `queueGarbage`:
 *
 * - A rising floor is not an attack. In the pending queue it could be
 *   cancelled by the player's own clear — the engine spends outgoing attack
 *   on the oldest batch first — and in the one variant with both a floor and
 *   attacks a player could cancel the floor instead of the garbage aimed at
 *   them.
 * - It would also count towards the incoming meter, which is for the attack
 *   aimed at you and not the floor under you.
 *
 * `everyTicks` is a positive integer; anything else is refused, because the
 * cadence decides when hashed state changes.
 */
export function risingFloorRule(everyTicks: number): TickRule {
  if (!Number.isSafeInteger(everyTicks) || everyTicks < 1) {
    throw new RangeError(
      `risingFloorRule: everyTicks must be a positive integer, got ${String(everyTicks)}`,
    );
  }
  return (state: GameState, tick: number): void => {
    if (tick > 0 && tick % everyTicks === 0) applyGarbageLines(state, 1);
  };
}

/** Ticks from `tick` until the floor's next row, for a countdown. Never zero. */
export function ticksUntilFloorRow(tick: number, everyTicks: number): number {
  return everyTicks - (tick % everyTicks);
}

/** Run a rule for the tick a state is about to play, unless the board has topped out. */
export function applyTickRule(state: GameState, rule: TickRule | null): void {
  if (rule !== null && !state.topOut) rule(state, state.tick);
}

/**
 * One tick of a game that has a tick rule and nothing else layered on it: the
 * rule, then the engine's own `step`.
 *
 * This is what a solo run steps with. A `SimDriver` does the same two things
 * in the same order, with the scheduled garbage before the rule and the curse
 * and gravity modifiers after it.
 */
export function stepWithRule(
  state: GameState,
  rule: TickRule | null,
  events: readonly InputEvent[] = [],
): StepResult {
  applyTickRule(state, rule);
  return step(state, events);
}
